import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useStripe } from '@stripe/react-stripe-js';
import { reduxForm, formValueSelector, InjectedFormProps } from 'redux-form';

import { validatePostcode } from '../../../../forms/helpers/validation';
import ConfirmationButton from '../../../ui/confirmationButton/ConfirmationButton';
import {
    REGISTER_DEBIT_CARD,
    HANDLE_STRIPE_FIELD_CHANGE,
    SAVE_CARD_NUMBER_ELEMENT
} from '../AddPaymentMethodModal.actions';
import { BillingPostcodeQuestion } from './BillingPostcodeQuestion';
import { CardNumberQuestion } from './CardNumberQuestion';
import { CVVQuestion } from './CVVQuestion';
import { ExpiryDateQuestion } from './ExpiryDateQuestion';
import styles from './LinkDebitCard.module.css';
import messages from './LinkDebitCard.messages';
import { Dispatch } from 'redux';
import { RootState } from '@iwProvider/configureStore';
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { FormField } from '@forms/formField';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    saveCardNumberElement: (cardNumberElement: StripeCardNumberElement) =>
        dispatch({
            type: SAVE_CARD_NUMBER_ELEMENT,
            cardNumberElement
        }),
    createToken: (
        stripe: Stripe,
        repaymentRole: string | null,
        billingPostcode: string
    ) =>
        dispatch({
            type: REGISTER_DEBIT_CARD,
            stripe,
            repaymentRole,
            billingPostcode
        }),
    handleFieldChange: (field: string, response: any) =>
        dispatch({
            type: HANDLE_STRIPE_FIELD_CHANGE,
            field: field,
            response: response
        })
});

const formConfiguration = {
    form: 'linkDebitCard',
    validate: (props: any) => ({
        billingPostcode: validatePostcode(
            messages.billingPostcodeError,
            props.billingPostcode
        )
    })
};

const selector = formValueSelector('linkDebitCard');

const mapStateToProps = (state: RootState) => ({
    ...state.addPaymentMethodModal,
    billingPostcode: selector(state, 'billingPostcode'),
    initialValues: {
        repaymentRole: true
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const LinkDebitCardForm = ({
    invalidCardType,
    invalidCard,
    invalidNumber,
    invalidCVV,
    invalidExpiryDate,
    linkingDebitCard,
    handleSubmit,
    registerCardStatus,
    createToken,
    billingPostcode,
    saveCardNumberElement,
    handleFieldChange,
    showSubText = true,
    showDefaultPaymentMethodCheckbox = false
}: {
    linkingDebitCard?: boolean;
    showSubText?: boolean;
    showDefaultPaymentMethodCheckbox?: boolean;
} & PropsFromRedux &
    InjectedFormProps) => {
    const handleChange = (field: string) => (response: any) => {
        handleFieldChange(field, response);
    };

    const disabledStates = [
        invalidCardType,
        invalidCard,
        invalidNumber,
        invalidCVV,
        invalidExpiryDate
    ];

    const stripe = useStripe();
    return (
        <form
            data-testid="link-debit-card-modal"
            className={styles.LinkDebitCardForm}
            id="payment-form"
            onSubmit={handleSubmit(
                ({
                    billingPostcode,
                    repaymentRole
                }: {
                    billingPostcode?: string;
                    repaymentRole?: string;
                }) =>
                    createToken(
                        stripe!,
                        repaymentRole ? 'primary' : null,
                        billingPostcode!
                    )
            )}
        >
            {showSubText && (
                <div className={styles.subText}>
                    <FormattedMessage
                        tagName="p"
                        {...messages.scheduledRepaymentsText}
                    />
                </div>
            )}

            <div className={styles.topRow}>
                <CardNumberQuestion
                    onReady={(element) => saveCardNumberElement(element)}
                    error={{
                        invalidNumber,
                        invalidCard,
                        invalidCardType
                    }}
                    onChange={handleChange('cardNumber')}
                />
                <ExpiryDateQuestion
                    error={invalidExpiryDate}
                    onChange={handleChange('expiryDate')}
                />
                <CVVQuestion
                    error={invalidCVV}
                    onChange={handleChange('CVV')}
                />
                <BillingPostcodeQuestion />
            </div>

            {showDefaultPaymentMethodCheckbox && (
                <div className="mb-2xl">
                    <FormField
                        name="repaymentRole"
                        type="checkbox"
                        label={
                            <FormattedMessage
                                {...messages.repaymentRoleLabel}
                            />
                        }
                    />
                </div>
            )}

            {registerCardStatus ? (
                <FormattedMessage {...messages.successfulLinkText} />
            ) : null}

            <div className={styles.confirmationButtonContainer}>
                <ConfirmationButton
                    type="submit"
                    status={registerCardStatus}
                    disabled={
                        !billingPostcode ||
                        disabledStates.some((element) => element === true)
                    }
                    loading={linkingDebitCard}
                    className={styles.confirmationButton}
                >
                    <FormattedMessage {...messages.linkCardButton} />
                </ConfirmationButton>
            </div>
        </form>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(
    reduxForm<
        object,
        {
            linkingDebitCard?: boolean;
            showSubText?: boolean;
            showDefaultPaymentMethodCheckbox?: boolean;
        } & PropsFromRedux
    >(formConfiguration)(LinkDebitCardForm)
);
